import React, { useEffect, useState } from 'react'
import Header from '../../components/Layout2/Header/Header'
import BreadCrumbs from '../../components/BreadCrumbs/BreadCrumbs'
import { Box, Container } from '@mui/material'
import ScrollToTop from '../../components/ScrollToTop/ScrollToTop'
import Footer from '../../components/Layout2/Footer/Footer'
import ChatPopup from '../../components/ChatPopup/ChatPopup'
import { useOptions } from '../../context/optionContext'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { ROUTES } from '../../constants'
import Home from '../../pages/Layout2/Home/Home'
import MyMachines from '../../pages/MyMachines/MyMachines'
import Products from '../../pages/Products/Products'
import SingleProduct from '../../pages/SingleProduct/SingleProduct'
import Recommendations from '../../pages/Recommendations/Recommendations'
import Cart from '../../pages/Cart/Cart'
import Checkout from '../../pages/Checkout/Checkout'
import OrderConfirmation from '../../pages/OrderConfirmation/OrderConfirmation'
import OrderHistory from '../../pages/OrderHistory/OrderHistory'
import Settings from '../../pages/Settings/Settings'
import Profile from '../../pages/Profile/Profile'
import Admin from '../../pages/Admin/Admin'
import Rspl from '../../pages/Rspl/Rspl'
import Documents from '../../pages/Documents/Documents'
import Requests from '../../pages/Requests/Requests'
import Support from '../../pages/Support/Support'
import Analytics from '../../pages/Analytics/Analytics'
import ProtectedRoute from '../../ProtectedRoute'
import AdminRoute from '../../AdminRoute'
import PDFProductLinkMachineFinder from '../../components/PDFProductLinkMachineFinder/PDFProductLinkMachineFinder'
import Services from '../../pages/Services/Services'
import SavedForLater from "../../pages/SavedForLater/SavedForLater";
import PageNotFound from '../../pages/PageNotFound/PageNotFound'
import AdminV2 from '../../pages/Admin/AdminV2'
import SearchResults from '../../pages/SearchResults/SearchResults'
import TrackOrder from '../../pages/TrackOrder/TrackOrder'
import OrderFailure from '../../pages/OrderFaliure/OrderFaliure'
import NotificationsPage from '../../pages/Notifications/Notifications'
import Parts from '../../pages/Layout2/Parts/Parts'
import Service from '../../pages/Layout2/Service/Service'
import DigitalSolutions from '../../pages/Layout2/DigitalSolutions/DigitalSolutions'
import Training from '../../pages/Layout2/Training/Training'
import Cases from '../../pages/Layout2/Cases/Cases'
import WorkOrders from '../../pages/Layout2/WorkOrders/WorkOrders'
import PVMDashboard from '../../pages/Layout2/PVMDashboard/PVMDashboard'
import PMDashboard from '../../pages/Layout2/PMDashboard/PMDashboard'
import SOSDashboard from '../../pages/Layout2/SOSDashboard/SOSDashboard'
import MachineDashboard from '../../pages/Layout2/MachineDashboard/MachineDashboard'
import DocumentDashboard from '../../pages/Layout2/DocumentDashboard/DocumentDashboard';
import TrainingDashboard from '../../pages/Layout2/TrainingDashboard/TrainingDashboard'
import ManageUpdate from '../../pages/Layout2/ManageUpdate/ManageUpdate'
import Documentation from '../../pages/Layout2/Documentation/Documentation'
import BasicTraining from '../../pages/Layout2/TrainingDashboard/BasicTraining'
import HistoricalContent from '../../components/HistoricalContent/HistoricalContent'
import NotAuthorized from '../../components/NotAuthorized/NotAuthorized'
import { useAuth } from '../../context/authContext'
import { useSite } from '../../context/siteContext'
import { setAnalyticsContext } from '../../utilities/analyticsService'
import { canAccessCart, canAccessChatbot, canAccessOrderAnalytics, canAccessSettings, canAccessUserAnalytics } from '../../utilities/pageAccess'
import UserAnalytics from '../../pages/UserAnalytics/UserAnalytics'

import BannerNotice from '../../components/BannerNotice/BannerNotice'
const Layout2 = () => {
    const location = useLocation();
    const { user, hasPermission } = useAuth();
    const { optionKeys } = useOptions();
    const [chatOpen, setChatOpen] = useState(false);
    const showChatbot = optionKeys?.enableChatbot !== "false" && canAccessChatbot(hasPermission, user);
    const {
        selectedSite,
        selectedSiteData,
        selectedSiteCurrency,
        selectedSiteLegalEnitity,
    } = useSite();

    // Measures the actual rendered header height so ChatPopup.css's fixed panel can
    // dock its top edge flush below the real navbar instead of a hardcoded guess.
    // Queries the AppBar's own DOM node directly by its stable className (rather
    // than wrapping <Header/> in a ref'd Box) — a wrapper div around a
    // position:"sticky" AppBar was found to break the header's own stickiness (it
    // started scrolling away instead of staying pinned), even though a plain
    // unstyled wrapper shouldn't in theory; not worth the risk of leaving that
    // extra div in place. This way <Header/>'s own rendered output is completely
    // untouched. ResizeObserver (not a one-time measurement) because the header's
    // height can change — banner notices, wrapped nav on narrower widths, etc.
    const [headerHeight, setHeaderHeight] = useState(64);
    useEffect(() => {
        const node = document.querySelector(".header-main");
        if (!node) return undefined;
        const observer = new ResizeObserver((entries) => {
            const height = entries?.[0]?.contentRect?.height;
            if (height) setHeaderHeight(height);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        if (!user && !selectedSite && !selectedSiteData) return;

        const roleName =
            user?.role?.role_name ||
            user?.role?.role_type ||
            user?.user_role_name ||
            user?.user_role ||
            (typeof user?.role === "string" ? user.role : "") ||
            "";

        setAnalyticsContext({
            // User context
            user_id: user?.user_id || user?.id || "",
            user_name:
                user?.user_display_name ||
                user?.user_name ||
                `${user?.user_first_name || ""} ${user?.user_last_name || ""}`.trim() ||
                "",
            user_email: user?.user_email || user?.email || "",
            role_id: user?.role_id || user?.role?.role_id || "",
            user_role: roleName,

            // Bakery context
            customer_id: selectedSiteData?.cus_id || "",
            customer_name:
                selectedSiteData?.parent_customer_name ||
                selectedSiteData?.customer_name ||
                "",
            customer_number: selectedSiteData?.customer_number || "",

            // Location context
            site_id: selectedSiteData?.site_id || "",
            site_name:
                selectedSiteData?.customer_site_name ||
                selectedSiteData?.site_name ||
                selectedSite ||
                "",
            site_number:
                selectedSiteData?.customer_site_number ||
                selectedSite ||
                "",
            customer_site_number:
                selectedSiteData?.customer_site_number ||
                selectedSite ||
                "",

            // Legal entity / currency
            legal_entity:
                selectedSiteData?.site_legal_entity ||
                selectedSiteData?.legal_entity ||
                selectedSiteLegalEnitity ||
                "",
            site_legal_entity:
                selectedSiteData?.site_legal_entity ||
                selectedSiteData?.legal_entity ||
                selectedSiteLegalEnitity ||
                "",

            currency: selectedSiteCurrency || "",

            location:
                selectedSiteData?.customer_site_name ||
                selectedSiteData?.site_name ||
                selectedSite ||
                "",
        });
    }, [
        user,
        selectedSite,
        selectedSiteData,
        selectedSiteCurrency,
        selectedSiteLegalEnitity,
    ]);
    const noBreadcrumPaths = [ROUTES.HOME, ROUTES.HOME_ALIAS, "/admin-v2", "/admin"];
    return (
        <Box
            display="flex"
            flexDirection="column"
            minHeight="100vh"
            width={"100vw"}
            sx={{ "--chip-header-h": `${headerHeight}px` }}
        >
            <Header chatOpen={chatOpen} setChatOpen={setChatOpen} showChatbot={showChatbot} />
            {/* CHIP is a position:fixed panel (ChatPopup.css) — robust, independent of page
                scroll/height (no footer overlap, no conflict with pages that manage their own
                internal scroll like Documentation). To get the iserv SHRINK behavior without
                touching page scroll, the content column reserves the panel's width via
                margin-right:var(--chip-panel-w) (set by ChatPopup.js, 0 when closed) so the page
                reflows into the remaining space instead of being covered. Footer is this column's
                own last child and shrinks with it. */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minWidth: 0,
                    marginRight: "var(--chip-panel-w, 0px)",
                    transition: "margin-right 0.28s ease",
                }}
            >
                <BannerNotice />

                {!noBreadcrumPaths.includes(location.pathname) && (
                    <BreadCrumbs />
                )}
                <Container maxWidth={false} component="main" className="main-container">
                        <Routes>
                            <Route path={ROUTES.HOME} element={<Home />} />
                            <Route path={"/parts"} element={<Parts />} />
                            <Route path={"/documentation/:machineId?"} element={<Documentation />} />
                            <Route path="/documentation/historical-content" element={<HistoricalContent />} />
                            <Route path={"/service"} element={<Service />} />
                            {/* <Route path={"/training"} element={<Training />} /> */}
                            <Route path="/training" element={<TrainingDashboard />} />
                            <Route path="/training/basic-training" element={<BasicTraining />} />
                            <Route path={"/service/digital-solutions"} element={<DigitalSolutions />} />
                            <Route path={"/service/digital-solutions/preventive-dashboard"} element={<PVMDashboard />} />
                            <Route path={"/service/digital-solutions/predictive-dashboard"} element={<PMDashboard />} />
                            <Route path={"/service/digital-solutions/sustainable-oven-service-dashboard"} element={<SOSDashboard />} />
                            <Route path={"/service/digital-solutions/machine-dashboard"} element={<MachineDashboard />} />
                            <Route path={"/service/cases"} element={<Cases />} />
                            <Route path={"/service/workorders"} element={<WorkOrders />} />
                            <Route path={ROUTES.HOME_ALIAS} element={<Home />} />
                            <Route path={ROUTES.MACHINES} element={<MyMachines />} />
                            <Route path={`/${ROUTES.MACHINES}/:machineID`} element={<Products />} />
                            <Route path={`/${ROUTES.MACHINES}/:machineID/:productID`} element={<SingleProduct />} />
                            <Route path={ROUTES.RECOMMENDATIONS} element={<Recommendations />} />
                            {/* SOW item F — merged into the admin "Recommendation Approval" page
                                (UAT 2026-08-17): this used to be its own standalone page, duplicating
                                the admin drill-down with a different scope. Redirected rather than
                                deleted outright so any bookmarked/shared link still lands somewhere
                                real instead of a dead route. */}
                            <Route path={ROUTES.CCR_RECOMMENDATIONS}
                                element={<Navigate to={`${ROUTES.ADMIN}?tab=recommendation_acceptance`} replace />} />
                            <Route path={ROUTES.CART} element={<Cart />} />
                            <Route path={ROUTES.CHECKOUT} element={<Checkout />} />
                            <Route path={ROUTES.ORDER_RECEIVED} element={<OrderConfirmation />} />
                            <Route path={"/order-failure"} element={<OrderFailure />} />
                            <Route path={ROUTES.ORDER_ERROR} element={<OrderConfirmation />} />
                            <Route path={ROUTES.ORDER_HISTORY} element={<OrderHistory />} />
                            <Route path={ROUTES.ORDER_PRODUCT_DETAILS} element={<OrderHistory />} />
                            <Route path={ROUTES.ORDERS_DETAILS} element={<OrderHistory />} />
                            <Route path={ROUTES.TRACK_ORDER} element={<TrackOrder />} />
                            <Route path={ROUTES.SETTINGS} element={canAccessSettings(user) ? <Settings /> : <NotAuthorized />} />
                            <Route path={ROUTES.PROFILE} element={<Profile />} />
                            <Route path={ROUTES.ADMIN} element={<Admin />} />
                            <Route path={ROUTES.RSPL} element={<Rspl />} />
                            <Route path={ROUTES.DOCUMENT} element={<Documents />} />
                            <Route path={ROUTES.DOCUMENT_DETAILS} element={<Documents />} />
                            <Route path={ROUTES.REQUESTS} element={<Requests />} />
                            <Route path={ROUTES.SUPPORT} element={<Support />} />
                            <Route path={ROUTES.SAVED_FOR_LATER} element={<SavedForLater />} />
                            <Route path={ROUTES.ANALYTICS} element={canAccessOrderAnalytics(hasPermission, user) ? <Analytics /> : <NotAuthorized />} />
                            <Route path={ROUTES.USER_ANALYTICS} element={canAccessUserAnalytics(hasPermission, user) ? <UserAnalytics /> : <NotAuthorized />} />
                            <Route path={ROUTES.SERVICES} element={<Services />} />
                            <Route path={ROUTES.SEARCH_RESULTS} element={<SearchResults />} />
                            <Route path={ROUTES.NOTIFICATIONS} element={<NotificationsPage />} />
                            <Route path={`${ROUTES.FINDER}/:product_id`} element={<PDFProductLinkMachineFinder />} />
                            <Route path={ROUTES.PAGE_NOT_FOUND} element={<PageNotFound />} />
                        </Routes>
                    </Container>
                    <Footer />
                </Box>
            {showChatbot && <ChatPopup open={chatOpen} onClose={() => setChatOpen(false)} />}
            <ScrollToTop />
        </Box>
    )
}

export default Layout2
